import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { twMerge } from 'tailwind-merge'
import { MoonpayDeposit } from '~/features/deposit/moonpay'
import { Deposit as DepositSolanaUsdc } from '~/features/deposit/solana_usdc'
import { useBalance } from '../api'
import { useCurrency } from '../hooks'
import { useBankGates } from '../hooks/useBankGates'
import { useDepositWithdraw } from '../hooks/useDepositWithdraw'
import { Funds } from './Funds'
import { CurrencyIcon, SelectCurrency } from './SelectCurrency'
import { Withdraw } from './Withdraw'

const actions = ['deposit', 'withdraw']

/**
 * What you have, at the top of the dialog that changes it.
 *
 * The modal asked people to decide how much to move without telling them where they were starting
 * from — the figure was in the bar behind it, which is covered on a phone.
 *
 * Two numbers and no more. Bonus is only drawn when there is some, so the common case is a single
 * figure; the split is worth showing at all because bonus funds cannot be spent everywhere
 * spendable ones can, and this dialog is where someone is reasoning about the difference.
 *
 * Nothing is rendered until the balance lands. A row of dashes that becomes a number is a layout
 * shift at the top of a dialog someone is already reading.
 *
 * The figures go through `Funds.Spendable` and `Funds.Bonus` rather than being written out here.
 * They were plain white text, so this dialog was the one place on the site where bonus money did
 * not look like bonus money -- no gift mark, no amber -- while the header, the drawer and the
 * dashboard all say it the same way.
 *
 * Exact amounts, never compacted: this is the screen where someone decides how much to move.
 */
const BalanceSummary = () => {
    const { data: balance, isFetched } = useBalance()
    if (!isFetched) return null

    const bonus = balance?.airdrop ?? 0

    return (
        <div className='col-start-1 col-end-[-1] flex flex-row flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-white/10 pb-3 -mt-1'>
            <div className='flex flex-row items-baseline gap-2'>
                <span className='text-[0.625rem] uppercase tracking-[0.2em] text-white/45'>Balance</span>
                <Funds.Spendable amount={balance?.usdc} className='text-sm tabular-nums' />
            </div>
            {bonus > 0 && (
                <div className='flex flex-row items-baseline gap-2'>
                    <span className='text-[0.625rem] uppercase tracking-[0.2em] text-white/45'>Bonus</span>
                    <Funds.Bonus amount={bonus} showLabel={false} className='text-sm tabular-nums' />
                </div>
            )}
        </div>
    )
}

export const DepositWithdraw = () => {
    const { action, setAction } = useDepositWithdraw()
    const { currency, setCurrency } = useCurrency()
    const { withdrawBlocked } = useBankGates()

    const switchAction = (e) => {
        setAction(e.target.value)
        setCurrency(undefined)
    }

    return (
        <div className='relative grid grid-cols-2 md:grid-cols-4 gap-4 p-4 md:p-6 bg-black border border-white/20 w-full max-w-xl'>
            <header className='col-start-1 col-end-[-1] flex flex-row items-center gap-3 min-h-[40px]'>
                {/*
                  Picking a method used to be one-way: the only route back to the list was to switch
                  to the other tab and switch back, which silently reset the form.
                */}
                {currency && (
                    <button
                        onClick={() => setCurrency(undefined)}
                        aria-label='Back to payment methods'
                        className='h-9 w-9 shrink-0 flex items-center justify-center border border-white/25 hover:border-white transition-colors'
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                )}
                <div className='flex flex-row items-center gap-3 min-w-0'>
                    {currency && <CurrencyIcon currency={currency} className='h-7 w-7 shrink-0' />}
                    <div className='min-w-0'>
                        <div className='uppercase text-lg md:text-xl font-bold leading-tight truncate'>{action}</div>
                        {currency && (
                            <div className='text-xs text-white/60 uppercase tracking-wide truncate'>
                                {currency.label || currency.name}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <BalanceSummary />

            {/*
              Withdraw is a real tab or it is not there to press.

              It was unconditional, which only stayed hidden because the header refused to open this
              modal at all while `WITHDRAWAL_ENABLED` was off. Now that depositing is correctly
              unaffected by that flag, this dialog is reachable in exactly the state where its second
              tab leads to a form whose RPC methods are not registered — so the tab has to say so.
            */}
            <div className='col-start-1 col-end-[-1] flex flex-col gap-1'>
                <div className='grid grid-cols-2 border border-white/20 p-1 gap-1'>
                    {actions.map((actionName) => {
                        const blocked = actionName === 'withdraw' ? withdrawBlocked : undefined
                        return (
                            <button
                                key={actionName}
                                disabled={!!blocked}
                                title={blocked}
                                className={twMerge(
                                    'uppercase text-sm md:text-base py-2 min-h-[44px] font-bold transition-colors',
                                    actionName === action
                                        ? 'bg-br1-gradient'
                                        : 'text-white/60 hover:text-white hover:bg-white/5',
                                    blocked && 'text-white/25 hover:bg-transparent hover:text-white/25 cursor-not-allowed'
                                )}
                                value={actionName}
                                onClick={blocked ? undefined : switchAction}
                            >
                                {actionName}
                            </button>
                        )
                    })}
                </div>
                {withdrawBlocked && (
                    <p className='text-2xs uppercase tracking-wider text-white/40'>{withdrawBlocked}</p>
                )}
            </div>

            {currency ? (
                <>
                    {action === 'withdraw' && <Withdraw />}
                    {action === 'deposit' && (
                        <div className='col-span-2 md:col-span-4'>
                            {currency?.moonpay ? <MoonpayDeposit /> : <DepositSolanaUsdc />}
                        </div>
                    )}
                </>
            ) : (
                <SelectCurrency />
            )}
        </div>
    )
}
